import axiosInstance from '@/core/api'
import type { paths } from '@/schema'

type GetTournamentsQuery =
	paths['/api/admin/tournaments']['get']['parameters']['query']
type GetTournamentsResponse =
	paths['/api/admin/tournaments']['get']['responses']['200']['content']['application/json']
type GetTournamentResponse =
	paths['/api/admin/tournaments/{id}']['get']['responses']['200']['content']['application/json']
type CreateTournamentBody =
	paths['/api/admin/tournaments']['post']['requestBody']['content']['application/json']
type CreateTournamentResponse =
	paths['/api/admin/tournaments']['post']['responses']['201']['content']['application/json']
type UpdateTournamentBody =
	paths['/api/admin/tournaments/{id}']['patch']['requestBody']['content']['application/json']
type UpdateTournamentResponse =
	paths['/api/admin/tournaments/{id}']['patch']['responses']['200']['content']['application/json']
type UpdateTournamentStatusBody =
	paths['/api/admin/tournaments/{id}/status']['patch']['requestBody']['content']['application/json']
type UpdateTournamentStatusResponse =
	paths['/api/admin/tournaments/{id}/status']['patch']['responses']['200']['content']['application/json']
type GetTournamentPlayersQuery =
	paths['/api/admin/tournaments/{id}/players']['get']['parameters']['query']
type GetTournamentPlayersResponse =
	paths['/api/admin/tournaments/{id}/players']['get']['responses']['200']['content']['application/json']
type ManualJoinTournamentBody =
	paths['/api/admin/tournaments/{id}/players']['post']['requestBody']['content']['application/json']
type ManualJoinTournamentResponse =
	paths['/api/admin/tournaments/{id}/players']['post']['responses']['201']['content']['application/json']
type FinalizeTournamentResponse =
	paths['/api/admin/tournaments/{id}/finalize']['post']['responses']['200']['content']['application/json']
type GetTournamentPresetsResponse = {
	presets: Array<{
		preset: string
		name: string
		description: string
		bannerImage: string
		minTurnover: number
		minDeposit: number
		minBetAmount: number
		maxTransactionDay: number
		allowedGameTypes: string
		prizes: Array<{
			rank: number
			prizeAmount: number
		}>
	}>
}
type CreateFromPresetBody =
	paths['/api/admin/tournaments/from-preset']['post']['requestBody']['content']['application/json']
type CreateFromPresetResponse =
	paths['/api/admin/tournaments/from-preset']['post']['responses']['201']['content']['application/json']

export class TournamentService {
	static async getTournaments(
		query?: GetTournamentsQuery,
	): Promise<GetTournamentsResponse> {
		const { data } = await axiosInstance.get<GetTournamentsResponse>(
			'/admin/tournaments',
			{
				params: query,
			},
		)
		return data
	}

	static async getTournamentById(id: string): Promise<GetTournamentResponse> {
		const { data } = await axiosInstance.get<GetTournamentResponse>(
			`/admin/tournaments/${id}`,
		)
		return data
	}

	static async createTournament(
		body: CreateTournamentBody,
	): Promise<CreateTournamentResponse> {
		const { data } = await axiosInstance.post<CreateTournamentResponse>(
			'/admin/tournaments',
			body,
		)
		return data
	}

	static async updateTournament(
		id: string,
		body: UpdateTournamentBody,
	): Promise<UpdateTournamentResponse> {
		const { data } = await axiosInstance.patch<UpdateTournamentResponse>(
			`/admin/tournaments/${id}`,
			body,
		)
		return data
	}

	static async updateTournamentStatus(
		id: string,
		body: UpdateTournamentStatusBody,
	): Promise<UpdateTournamentStatusResponse> {
		const { data } = await axiosInstance.patch<UpdateTournamentStatusResponse>(
			`/admin/tournaments/${id}/status`,
			body,
		)
		return data
	}

	static async deleteTournament(id: string): Promise<void> {
		await axiosInstance.delete(`/admin/tournaments/${id}`)
	}

	static async getTournamentPlayers(
		id: string,
		query?: GetTournamentPlayersQuery,
	): Promise<GetTournamentPlayersResponse> {
		const { data } = await axiosInstance.get<GetTournamentPlayersResponse>(
			`/admin/tournaments/${id}/players`,
			{
				params: query,
			},
		)
		return data
	}

	static async manualJoinTournament(
		id: string,
		body: ManualJoinTournamentBody,
	): Promise<ManualJoinTournamentResponse> {
		const { data } = await axiosInstance.post<ManualJoinTournamentResponse>(
			`/admin/tournaments/${id}/players`,
			body,
		)
		return data
	}

	static async finalizeTournament(
		id: string,
	): Promise<FinalizeTournamentResponse> {
		const { data } = await axiosInstance.post<FinalizeTournamentResponse>(
			`/admin/tournaments/${id}/finalize`,
		)
		return data
	}

	static async getTournamentPresets(): Promise<GetTournamentPresetsResponse> {
		const { data } = await axiosInstance.get<GetTournamentPresetsResponse>(
			'/admin/tournaments/presets',
		)
		return data
	}

	static async createTournamentFromPreset(
		body: CreateFromPresetBody,
	): Promise<CreateFromPresetResponse> {
		const { data } = await axiosInstance.post<CreateFromPresetResponse>(
			'/admin/tournaments/from-preset',
			body,
		)
		return data
	}
}
